/**
 * LeadTriageDashboard — Phase 4 (2026-05-26).
 *
 * Miss Deepika's only-screen workspace. Three tabs (Unassigned / Assigned /
 * Self-made), 9 columns matching Manak's screenshot + Application Type +
 * B2B/B2C. Per-cell "unverified" rendering as dashed-border empty cell with
 * tooltip. Inline assign-to-team dropdown with optimistic toast feedback.
 *
 * Talks to:
 *   GET  /api/v1/leads/triage-dashboard?tab=...&search=...&sort=...&limit=...
 *   POST /api/v1/leads/:id/assign-team   { teamId }
 *   GET  /api/v1/leads/triage-teams
 *   PATCH /api/v1/auth/me-update         { firstName, avatar, currentPassword, newPassword }
 *
 * Pure presentational with `fetch` — no extra deps. Tailwind classes only.
 */

import { useEffect, useMemo, useState } from 'react';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';
import { TierChip } from './TierChip';
import { LeadResponsesDrawer } from './LeadResponsesDrawer';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DashboardRow {
  id: string;
  createdAt: string;
  enquiryDate: string;
  source: string | null;
  name: string | null;
  phone: string | null;
  email: string | null;
  location: string | null;
  requirement: string | null;
  applicationType: string | null;
  businessSegment: string | null;
  verifiedStatus: 'verified' | 'unverified';
  verifiedBy: 'chatkart' | 'manual' | 'avika' | null;
  verifiedByName: string | null;
  assignedToName: string | null;
  teamName: string | null;
  // ── Avika (2026-06-10) enrichment ──────────────────────────────────────
  score: number | null;
  tier: 'HOT' | 'WARM' | 'COLD' | null;
  scoringModel: 'end_customer' | 'no_price' | 'b2b' | null;
  capacity: string | null;
  capacitySource: string | null;
  timeline: string | null;
  priceShared: boolean;
  priceRange: string | null;
  state: string | null;
  waLineLabel: 'line1' | 'line2' | null;
  isB2bPriority: boolean;
  isOutOfBudget: boolean;
}

interface VerifyForm {
  productCategory: string;
  applicationType: '' | 'Residential' | 'Commercial' | 'B2B';
  businessSegment: '' | 'B2B' | 'B2C';
  location: string;
  requirement: string;
  notes: string;
}
const BLANK_VERIFY_FORM: VerifyForm = {
  productCategory: '',
  applicationType: '',
  businessSegment: '',
  location: '',
  requirement: '',
  notes: '',
};

interface DashboardResponse {
  success: true;
  data: DashboardRow[];
  total: number;
  tab: string;
  limit: number;
  offset: number;
}

interface Team {
  id: string;
  name: string;
}

type Tab = 'unassigned' | 'assigned' | 'self_made';
const TABS: { key: Tab; label: string }[] = [
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'self_made', label: 'Self-made' },
];

// ─── Per-cell unverified rendering ───────────────────────────────────────────

function Cell({ value, dim = false }: { value: string | null | undefined; dim?: boolean }) {
  if (!value || value.trim().length === 0) {
    return (
      <span
        className="inline-flex items-center text-xs italic text-gray-400 border border-dashed border-gray-300 rounded px-1.5 py-0.5"
        title="Not yet verified by Ananya"
      >
        unverified
      </span>
    );
  }
  return <span className={dim ? 'text-gray-500' : 'text-gray-900'}>{value}</span>;
}

// Build a clean, copy-ready text summary of a lead (everything: name → number →
// requirement → score). Read-only — used by the Copy column.
function buildLeadSummary(r: DashboardRow): string {
  const L: string[] = [];
  L.push(`Lead: ${r.name || 'Unknown'}`);
  if (r.phone) L.push(`Phone: ${r.phone}`);
  if (r.email) L.push(`Email: ${r.email}`);
  L.push(`Enquiry date: ${formatDate(r.createdAt)}`);
  const line = r.waLineLabel === 'line1' ? 'Line 1' : r.waLineLabel === 'line2' ? 'Line 2' : null;
  L.push(`Source: ${r.source || 'Unknown'}${line ? ` (${line})` : ''}`);
  const loc = [r.location, r.state].filter(Boolean).join(', ');
  if (loc) L.push(`Location: ${loc}`);
  if (r.requirement) L.push(`Requirement: ${r.requirement}`);
  if (r.applicationType) L.push(`Application: ${r.applicationType}${r.businessSegment ? ` (${r.businessSegment})` : ''}`);
  if (r.capacity) L.push(`Capacity: ${r.capacity}`);
  if (r.timeline) L.push(`Timeline: ${r.timeline}`);
  if (r.priceShared) L.push(`Indicative price shared: ${r.priceRange || 'Yes'}`);
  if (r.score != null) {
    const flags = [r.isB2bPriority ? 'B2B PRIORITY' : '', r.isOutOfBudget ? 'OUT OF BUDGET' : ''].filter(Boolean).join(', ');
    L.push(`Lead score: ${r.score}${r.tier ? ` (${r.tier})` : ''}${flags ? ` — ${flags}` : ''}`);
  }
  if (r.scoringModel) L.push(`Scoring model: ${r.scoringModel}`);
  const verified = r.verifiedStatus === 'verified'
    ? `Verified${r.verifiedBy ? ` (${r.verifiedBy}${r.verifiedByName ? ': ' + r.verifiedByName : ''})` : ''}`
    : 'Unverified';
  L.push(`Status: ${verified}`);
  if (r.teamName || r.assignedToName) L.push(`Assigned: ${r.teamName || r.assignedToName}`);
  return L.join('\n');
}

// One-click copy-to-clipboard button (read-only). Shows a brief "Copied" state.
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try { document.execCommand('copy'); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      type="button"
      onClick={copy}
      className={`text-xs font-medium rounded px-2 py-0.5 border transition-colors ${copied ? 'text-green-700 border-green-400 bg-green-50' : 'text-gray-700 border-gray-300 hover:bg-gray-100'}`}
      title="Copy the full lead summary to clipboard"
    >
      {copied ? '✓ Copied' : 'Copy'}
    </button>
  );
}

function VerifiedBadge({ status }: { status: 'verified' | 'unverified' }) {
  if (status === 'verified') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded">
        <span className="w-1.5 h-1.5 bg-green-500 rounded-full" /> verified
      </span>
    );
  }
  return null;
}

// ─── Date formatter (DD-MMM matching screenshot "01-May") ───────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}-${MONTHS[d.getMonth()]}`;
}

// ─── Toast ───────────────────────────────────────────────────────────────────

interface Toast {
  id: string;
  text: string;
  undo?: () => void;
}

// ─── Filters + sort ──────────────────────────────────────────────────────────

type SortKey =
  | 'newest' | 'oldest'
  | 'name_asc' | 'name_desc'
  | 'source_asc' | 'source_desc'
  | 'location_asc' | 'location_desc'
  // Avika (2026-06-10) — lead-score sorts.
  | 'score_desc' | 'score_asc';

type VerifiedFilter = 'any' | 'verified' | 'unverified';
type VerifiedSourceFilter = 'any' | 'chatkart' | 'manual';

// Avika (2026-06-10) — WhatsApp line filter.
type LineFilter = 'all' | 'line1' | 'line2';

interface FilterState {
  dateFrom: string;
  dateTo: string;
  sources: string[];
  applicationTypes: string[];
  businessSegments: string[];
  verified: VerifiedFilter;
  verifiedSource: VerifiedSourceFilter;
  location: string;
}
const BLANK_FILTERS: FilterState = {
  dateFrom: '', dateTo: '',
  sources: [], applicationTypes: [], businessSegments: [],
  verified: 'any', verifiedSource: 'any',
  location: '',
};

const SOURCE_OPTIONS = ['WEBSITE', 'INDIAMART', 'AI CHATBOX'];
const APP_TYPE_OPTIONS = ['Residential', 'Commercial', 'B2B'];
const SEGMENT_OPTIONS = ['B2B', 'B2C'];

// Date range presets — returns [from, to] in YYYY-MM-DD
function presetRange(preset: string): [string, string] {
  const today = new Date();
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const to = fmt(today);
  if (preset === 'today') return [to, to];
  if (preset === '7d')  { const d = new Date(today); d.setDate(d.getDate() - 7);  return [fmt(d), to]; }
  if (preset === '30d') { const d = new Date(today); d.setDate(d.getDate() - 30); return [fmt(d), to]; }
  if (preset === 'mtd') { const d = new Date(today.getFullYear(), today.getMonth(), 1); return [fmt(d), to]; }
  return ['', ''];
}

// ─── Component ───────────────────────────────────────────────────────────────

export function LeadTriageDashboard() {
  const [tab, setTab] = useState<Tab>('unassigned');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<DashboardRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [verifyOpenFor, setVerifyOpenFor] = useState<DashboardRow | null>(null);
  const [verifyForm, setVerifyForm] = useState<VerifyForm>(BLANK_VERIFY_FORM);
  const [verifySubmitting, setVerifySubmitting] = useState(false);
  const [filters, setFilters] = useState<FilterState>(BLANK_FILTERS);
  // Default to latest-first (newest enquiry on top). Score sort stays a toggle.
  const [sortKey, setSortKey] = useState<SortKey>('newest');
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Avika (2026-06-10) — WhatsApp line filter + responses drawer.
  const [lineFilter, setLineFilter] = useState<LineFilter>('all');
  const [responsesOpenFor, setResponsesOpenFor] = useState<DashboardRow | null>(null);

  // Load teams once
  useEffect(() => {
    let cancelled = false;
    safeGet<{ success: true; data: Team[] }>('/leads/triage-teams')
      .then((r) => { if (!cancelled && r?.data) setTeams(r.data); })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, []);

  // Load dashboard data
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const q = new URLSearchParams({ tab, sort: sortKey, limit: '50' });
    if (search.trim().length >= 2) q.set('search', search.trim());
    if (filters.dateFrom)              q.set('dateFrom', filters.dateFrom);
    if (filters.dateTo)                q.set('dateTo', filters.dateTo);
    if (filters.sources.length)        q.set('source', filters.sources.join(','));
    if (filters.applicationTypes.length) q.set('applicationType', filters.applicationTypes.join(','));
    if (filters.businessSegments.length) q.set('businessSegment', filters.businessSegments.join(','));
    if (filters.location.trim().length >= 2) q.set('location', filters.location.trim());
    if (filters.verified !== 'any')         q.set('verified', filters.verified);
    if (filters.verifiedSource !== 'any')   q.set('verifiedSource', filters.verifiedSource);
    if (lineFilter !== 'all')               q.set('line', lineFilter);
    safeGet<DashboardResponse>(`/leads/triage-dashboard?${q.toString()}`)
      .then((r) => {
        if (cancelled) return;
        if (r && (r as any).data) {
          setRows((r as any).data as DashboardRow[]);
          setTotal((r as any).total ?? 0);
        } else {
          setRows([]); setTotal(0);
        }
      })
      .catch((e) => { if (!cancelled) setError(String(e?.message ?? e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tab, search, sortKey, filters, lineFilter]);

  // Inline assign
  async function assignToTeam(leadId: string, teamId: string, teamName: string) {
    // Optimistic — move row out of Unassigned immediately
    const prev = rows;
    setRows(rows.filter((r) => r.id !== leadId));
    try {
      const res = await fetch(`/api/v1/leads/${leadId}/assign-team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teamId }),
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const toastId = Math.random().toString(36).slice(2);
      setToasts((t) => [
        ...t,
        {
          id: toastId,
          text: `Assigned to ${teamName}`,
          undo: () => {
            // Optimistic rollback — re-add the row + try to unassign
            setRows((r) => [prev.find((p) => p.id === leadId)!, ...r]);
            fetch(`/api/v1/leads/${leadId}/assign-team`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ teamId: '' }),
              credentials: 'include',
            }).catch(() => { /* best-effort undo */ });
            setToasts((tt) => tt.filter((x) => x.id !== toastId));
          },
        },
      ]);
      setTimeout(() => setToasts((tt) => tt.filter((x) => x.id !== toastId)), 5000);
    } catch (e) {
      // Rollback
      setRows(prev);
      setError(`Failed to assign: ${(e as Error).message}`);
    }
  }

  // Manual verify submit handler
  async function submitVerify() {
    if (!verifyOpenFor) return;
    setVerifySubmitting(true);
    try {
      const payload: Record<string, string> = {};
      if (verifyForm.productCategory.trim()) payload.productCategory = verifyForm.productCategory.trim();
      if (verifyForm.applicationType)        payload.applicationType = verifyForm.applicationType;
      if (verifyForm.businessSegment)        payload.businessSegment = verifyForm.businessSegment;
      if (verifyForm.location.trim())        payload.location = verifyForm.location.trim();
      if (verifyForm.requirement.trim())     payload.requirement = verifyForm.requirement.trim();
      if (verifyForm.notes.trim())           payload.notes = verifyForm.notes.trim();
      const res = await fetch(`/api/v1/leads/${verifyOpenFor.id}/verify-manually`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const verifiedByName = json?.data?.verifiedByName ?? null;
      // Update the row inline
      setRows((prev) => prev.map((row) => row.id === verifyOpenFor.id
        ? {
            ...row,
            ...(payload.location          ? { location: payload.location } : {}),
            ...(payload.requirement       ? { requirement: payload.requirement } : {}),
            ...(payload.applicationType   ? { applicationType: payload.applicationType } : {}),
            ...(payload.businessSegment   ? { businessSegment: payload.businessSegment } : {}),
            verifiedStatus: 'verified',
            verifiedBy: 'manual',
            verifiedByName,
          }
        : row));
      const toastId = Math.random().toString(36).slice(2);
      setToasts((t) => [...t, { id: toastId, text: `Verified ${verifyOpenFor.name ?? verifyOpenFor.phone ?? 'lead'}` }]);
      setTimeout(() => setToasts((tt) => tt.filter((x) => x.id !== toastId)), 4000);
      setVerifyOpenFor(null);
      setVerifyForm(BLANK_VERIFY_FORM);
    } catch (e) {
      setError(`Verify failed: ${(e as Error).message}`);
    } finally {
      setVerifySubmitting(false);
    }
  }

  const tabCounts = useMemo(() => ({ [tab]: total }), [tab, total]);

  return (
    <div className="p-4 md:p-6 max-w-screen-2xl mx-auto">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold text-gray-900">Lead Triage Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">
          Assign incoming leads to the right team. {total > 0 && (
            <span className="font-medium text-gray-700">{total} on this tab.</span>
          )}
        </p>
      </header>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t.label}
            {tab === t.key && total > 0 && (
              <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                {total}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search + Filter bar */}
      <div className="mb-3 flex flex-wrap gap-2 items-center">
        <input
          type="text"
          placeholder="Search by name, phone, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full md:w-72 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {/* Date preset */}
        <select
          value={
            filters.dateFrom === '' && filters.dateTo === '' ? 'all' :
            (() => {
              for (const p of ['today','7d','30d','mtd']) {
                const [f,t] = presetRange(p);
                if (filters.dateFrom === f && filters.dateTo === t) return p;
              }
              return 'custom';
            })()
          }
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'all')         setFilters({ ...filters, dateFrom: '', dateTo: '' });
            else if (v === 'custom') { /* keep current */ }
            else {
              const [from, to] = presetRange(v);
              setFilters({ ...filters, dateFrom: from, dateTo: to });
            }
          }}
          className="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
          title="Date range"
        >
          <option value="all">All time</option>
          <option value="today">Today</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="mtd">Month to date</option>
          <option value="custom">Custom…</option>
        </select>
        <input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
          className="text-xs border border-gray-300 rounded px-2 py-1.5"
          title="From date"
        />
        <input
          type="date"
          value={filters.dateTo}
          onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
          className="text-xs border border-gray-300 rounded px-2 py-1.5"
          title="To date"
        />

        {/* Source chips */}
        <div className="flex items-center gap-1">
          {SOURCE_OPTIONS.map((s) => {
            const active = filters.sources.includes(s);
            return (
              <button
                key={s}
                onClick={() => setFilters({
                  ...filters,
                  sources: active ? filters.sources.filter((x) => x !== s) : [...filters.sources, s],
                })}
                className={`text-xs px-2 py-1 rounded border transition-colors ${
                  active
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                }`}
              >{s}</button>
            );
          })}
        </div>

        {/* Verified status */}
        <select
          value={filters.verified}
          onChange={(e) => setFilters({ ...filters, verified: e.target.value as VerifiedFilter })}
          className="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
          title="Verified status"
        >
          <option value="any">Verified: any</option>
          <option value="verified">Verified only</option>
          <option value="unverified">Unverified only</option>
        </select>

        {/* Avika — WhatsApp line filter */}
        <div className="flex items-center gap-1" title="WhatsApp number / line">
          <span className="text-[11px] text-gray-500">Line:</span>
          {(['all', 'line1', 'line2'] as LineFilter[]).map((l) => (
            <button
              key={l}
              onClick={() => setLineFilter(l)}
              className={`text-xs px-2 py-1 rounded border transition-colors ${
                lineFilter === l
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
              }`}
            >
              {l === 'all' ? 'All' : l === 'line1' ? 'Line 1' : 'Line 2'}
            </button>
          ))}
        </div>

        {/* Avika — Score sort toggle */}
        <button
          onClick={() => setSortKey(sortKey === 'score_desc' ? 'score_asc' : 'score_desc')}
          className={`text-xs px-2 py-1.5 rounded border transition-colors ${
            sortKey === 'score_desc' || sortKey === 'score_asc'
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
          }`}
          title="Sort by lead score"
        >
          Score {sortKey === 'score_desc' ? '↓' : sortKey === 'score_asc' ? '↑' : '↕'}
        </button>

        {/* More filters toggle */}
        <button
          onClick={() => setFiltersOpen((o) => !o)}
          className="text-xs px-2 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
        >
          {filtersOpen ? 'Less ▴' : 'More ▾'}
        </button>

        {/* Clear all (only when filter active) */}
        {(filters.dateFrom || filters.dateTo || filters.sources.length || filters.applicationTypes.length ||
          filters.businessSegments.length || filters.location || filters.verified !== 'any' ||
          filters.verifiedSource !== 'any' || lineFilter !== 'all') && (
          <button
            onClick={() => { setFilters(BLANK_FILTERS); setLineFilter('all'); }}
            className="text-xs text-blue-700 hover:text-blue-900 underline-offset-2 hover:underline"
          >Clear all</button>
        )}
      </div>

      {/* Expanded filter row */}
      {filtersOpen && (
        <div className="mb-3 p-3 border border-gray-200 rounded-md bg-gray-50 flex flex-wrap gap-3 items-center">
          {/* Application type chips */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 mr-1">Application:</span>
            {APP_TYPE_OPTIONS.map((s) => {
              const active = filters.applicationTypes.includes(s);
              return (
                <button
                  key={s}
                  onClick={() => setFilters({
                    ...filters,
                    applicationTypes: active ? filters.applicationTypes.filter((x) => x !== s) : [...filters.applicationTypes, s],
                  })}
                  className={`text-xs px-2 py-1 rounded border ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                >{s}</button>
              );
            })}
          </div>
          {/* B2B/B2C chips */}
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 mr-1">Segment:</span>
            {SEGMENT_OPTIONS.map((s) => {
              const active = filters.businessSegments.includes(s);
              return (
                <button
                  key={s}
                  onClick={() => setFilters({
                    ...filters,
                    businessSegments: active ? filters.businessSegments.filter((x) => x !== s) : [...filters.businessSegments, s],
                  })}
                  className={`text-xs px-2 py-1 rounded border ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}
                >{s}</button>
              );
            })}
          </div>
          {/* Location */}
          <label className="text-xs flex items-center gap-1">
            <span className="text-gray-500">Location:</span>
            <input
              type="text"
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              placeholder="e.g. Panipat"
              className="text-xs border border-gray-300 rounded px-2 py-1 w-32"
            />
          </label>
          {/* Verified source */}
          <select
            value={filters.verifiedSource}
            onChange={(e) => setFilters({ ...filters, verifiedSource: e.target.value as VerifiedSourceFilter })}
            className="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
            title="Verified by"
          >
            <option value="any">Verified by: any</option>
            <option value="chatkart">ChatKart (Ananya)</option>
            <option value="manual">Manual (rep call)</option>
          </select>
        </div>
      )}

      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Sl. No.</th>
              <th
                className="text-left px-3 py-2 font-semibold text-gray-700 cursor-pointer select-none hover:text-blue-700"
                onClick={() => setSortKey(sortKey === 'newest' ? 'oldest' : 'newest')}
              >
                Enquiry Date {sortKey === 'newest' ? '↓' : sortKey === 'oldest' ? '↑' : '↕'}
              </th>
              <th
                className="text-left px-3 py-2 font-semibold text-gray-700 cursor-pointer select-none hover:text-blue-700"
                onClick={() => setSortKey(sortKey === 'source_asc' ? 'source_desc' : 'source_asc')}
              >
                Enquiry Source {sortKey === 'source_asc' ? '↑' : sortKey === 'source_desc' ? '↓' : '↕'}
              </th>
              <th
                className="text-left px-3 py-2 font-semibold text-gray-700 cursor-pointer select-none hover:text-blue-700"
                onClick={() => setSortKey(sortKey === 'name_asc' ? 'name_desc' : 'name_asc')}
              >
                Customer Name {sortKey === 'name_asc' ? '↑' : sortKey === 'name_desc' ? '↓' : '↕'}
              </th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Contact Number</th>
              <th
                className="text-left px-3 py-2 font-semibold text-gray-700 cursor-pointer select-none hover:text-blue-700"
                onClick={() => setSortKey(sortKey === 'location_asc' ? 'location_desc' : 'location_asc')}
              >
                Location / City {sortKey === 'location_asc' ? '↑' : sortKey === 'location_desc' ? '↓' : '↕'}
              </th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Requirement</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Application Type</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">B2B / B2C</th>
              {/* ── Avika (2026-06-10) columns ─────────────────────────────── */}
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Line</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Capacity</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Timeline</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Price shared</th>
              <th
                className="text-left px-3 py-2 font-semibold text-gray-700 cursor-pointer select-none hover:text-blue-700"
                onClick={() => setSortKey(sortKey === 'score_desc' ? 'score_asc' : 'score_desc')}
              >
                Lead Score {sortKey === 'score_desc' ? '↓' : sortKey === 'score_asc' ? '↑' : '↕'}
              </th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Tier</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Responses</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Copy</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Verified</th>
              <th className="text-left px-3 py-2 font-semibold text-gray-700">
                {tab === 'unassigned' ? 'Assign To Team' : tab === 'assigned' ? 'Team' : 'Created By'}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <>
                {Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-100 animate-pulse">
                    {Array.from({ length: 18 }).map((__, j) => (
                      <td key={j} className="px-3 py-3">
                        <div className="h-3 bg-gray-100 rounded w-20" />
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={19} className="px-3 py-12 text-center text-sm text-gray-500">
                  {tab === 'unassigned' && (
                    <>
                      All caught up. Ananya will surface new leads here within 5 min.<br />
                      <span className="text-xs">Last sync: just now.</span>
                    </>
                  )}
                  {tab === 'assigned' && 'No leads have been assigned yet.'}
                  {tab === 'self_made' &&
                    'Self-made leads appear when reps add them from the AURA Sales app. Coming soon.'}
                </td>
              </tr>
            )}

            {!loading && rows.map((r, idx) => (
              <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                <td className="px-3 py-2 text-gray-700">{formatDate(r.createdAt)}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        r.verifiedStatus === 'verified' ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                      title={r.verifiedStatus === 'verified' ? 'Verified by Ananya' : 'Unverified'}
                    />
                    <Cell value={r.source} />
                  </div>
                </td>
                <td className="px-3 py-2"><Cell value={r.name} /></td>
                <td className="px-3 py-2 font-mono text-xs text-gray-800">{r.phone || <Cell value={null} />}</td>
                <td className="px-3 py-2"><Cell value={r.location} /></td>
                <td className="px-3 py-2"><Cell value={r.requirement} /></td>
                <td className="px-3 py-2"><Cell value={r.applicationType} /></td>
                <td className="px-3 py-2"><Cell value={r.businessSegment} /></td>
                {/* ── Avika (2026-06-10) cells ─────────────────────────────── */}
                <td className="px-3 py-2">
                  {r.waLineLabel ? (
                    <span className="inline-flex items-center text-xs font-medium text-gray-700 bg-gray-100 rounded px-1.5 py-0.5">
                      {r.waLineLabel === 'line1' ? 'Line 1' : r.waLineLabel === 'line2' ? 'Line 2' : r.waLineLabel}
                    </span>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-3 py-2"><Cell value={r.capacity} /></td>
                <td className="px-3 py-2"><Cell value={r.timeline} /></td>
                <td className="px-3 py-2">
                  {r.priceShared ? (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                      {r.priceRange || 'Yes'}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">No</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {r.score == null ? (
                    <span className="text-gray-300">—</span>
                  ) : (
                    <span className="font-semibold text-gray-900">{r.score}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <TierChip tier={r.tier} isB2bPriority={r.isB2bPriority} isOutOfBudget={r.isOutOfBudget} />
                </td>
                <td className="px-3 py-2">
                  {r.verifiedBy === 'avika' || r.score != null ? (
                    <button
                      onClick={() => setResponsesOpenFor(r)}
                      className="text-xs font-medium text-blue-700 hover:text-blue-900 hover:bg-blue-50 border border-blue-300 hover:border-blue-500 rounded px-2 py-0.5 transition-colors"
                      title="View the full WhatsApp Q&A + score breakdown"
                    >
                      Responses
                    </button>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className="px-3 py-2"><CopyButton text={buildLeadSummary(r)} /></td>
                <td className="px-3 py-2">
                  {r.verifiedStatus === 'verified' ? (
                    r.verifiedBy === 'avika' ? (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 bg-violet-50 px-2 py-0.5 rounded"
                        title="Qualified by Avika (ChatKart line 2)"
                      >
                        <span className="w-1.5 h-1.5 bg-violet-500 rounded-full" />
                        Avika
                      </span>
                    ) : r.verifiedBy === 'manual' ? (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded"
                        title={`Manually verified${r.verifiedByName ? ' by ' + r.verifiedByName : ''}`}
                      >
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                        Manual{r.verifiedByName ? ` · ${r.verifiedByName}` : ''}
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded"
                        title="Verified by Ananya (ChatKart)"
                      >
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                        ChatKart
                      </span>
                    )
                  ) : (
                    <button
                      onClick={() => { setVerifyOpenFor(r); setVerifyForm(BLANK_VERIFY_FORM); }}
                      className="text-xs font-medium text-blue-700 hover:text-blue-900 hover:bg-blue-50 border border-blue-300 hover:border-blue-500 rounded px-2 py-0.5 transition-colors"
                      title="Call the customer and record their answers"
                    >
                      Verify
                    </button>
                  )}
                </td>
                <td className="px-3 py-2">
                  {tab === 'unassigned' ? (
                    <select
                      className="text-xs border border-gray-300 rounded px-2 py-1 bg-white hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      defaultValue=""
                      onChange={(e) => {
                        const teamId = e.target.value;
                        if (!teamId) return;
                        const team = teams.find((t) => t.id === teamId);
                        if (team) assignToTeam(r.id, teamId, team.name);
                        e.target.value = '';
                      }}
                    >
                      <option value="">Assign to ▾</option>
                      {teams.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  ) : tab === 'assigned' ? (
                    <Cell value={r.teamName || r.assignedToName} />
                  ) : (
                    <Cell value={r.assignedToName} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Avika Responses Drawer */}
      <LeadResponsesDrawer
        leadId={responsesOpenFor?.id ?? null}
        onClose={() => setResponsesOpenFor(null)}
      />

      {/* Manual Verify Modal */}
      {verifyOpenFor && (
        <div
          className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4"
          onClick={() => !verifySubmitting && setVerifyOpenFor(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Verify lead manually</h2>
              <button
                onClick={() => setVerifyOpenFor(null)}
                disabled={verifySubmitting}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none disabled:opacity-50"
                aria-label="Close"
              >×</button>
            </div>
            <div className="mb-3 text-sm">
              <div className="font-medium text-gray-900">{verifyOpenFor.name || '(no name)'}</div>
              <div className="font-mono text-xs text-gray-600">{verifyOpenFor.phone || '(no phone)'}</div>
              <div className="text-xs text-gray-500 mt-1">
                Call the customer, ask their requirement, and record the answers below.
              </div>
            </div>
            <div className="space-y-3">
              <label className="block text-sm">
                <span className="text-gray-700">Product / category</span>
                <input
                  type="text"
                  value={verifyForm.productCategory}
                  onChange={(e) => setVerifyForm((f) => ({ ...f, productCategory: e.target.value }))}
                  placeholder="e.g. SS Water Tank"
                  className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-gray-700">Application type</span>
                  <select
                    value={verifyForm.applicationType}
                    onChange={(e) => setVerifyForm((f) => ({ ...f, applicationType: e.target.value as VerifyForm['applicationType'] }))}
                    className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 rounded bg-white"
                  >
                    <option value="">—</option>
                    <option value="Residential">Residential</option>
                    <option value="Commercial">Commercial</option>
                    <option value="B2B">B2B</option>
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-gray-700">B2B / B2C</span>
                  <select
                    value={verifyForm.businessSegment}
                    onChange={(e) => setVerifyForm((f) => ({ ...f, businessSegment: e.target.value as VerifyForm['businessSegment'] }))}
                    className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 rounded bg-white"
                  >
                    <option value="">—</option>
                    <option value="B2B">B2B</option>
                    <option value="B2C">B2C</option>
                  </select>
                </label>
              </div>
              <label className="block text-sm">
                <span className="text-gray-700">Location / city</span>
                <input
                  type="text"
                  value={verifyForm.location}
                  onChange={(e) => setVerifyForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder="e.g. Panipat"
                  className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-700">Requirement</span>
                <textarea
                  rows={2}
                  value={verifyForm.requirement}
                  onChange={(e) => setVerifyForm((f) => ({ ...f, requirement: e.target.value }))}
                  placeholder="What did the customer ask for?"
                  className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-700">Notes (internal)</span>
                <textarea
                  rows={2}
                  value={verifyForm.notes}
                  onChange={(e) => setVerifyForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Any context the team should know"
                  className="mt-1 w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setVerifyOpenFor(null)}
                disabled={verifySubmitting}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >Cancel</button>
              <button
                onClick={submitVerify}
                disabled={verifySubmitting}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >{verifySubmitting ? 'Verifying…' : 'Mark verified'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Toasts */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-2 z-50">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="bg-gray-900 text-white text-sm rounded-lg shadow-lg px-4 py-3 flex items-center gap-3"
          >
            <span>{t.text}</span>
            {t.undo && (
              <button
                onClick={t.undo}
                className="text-blue-300 hover:text-blue-100 font-medium underline-offset-2 hover:underline"
              >
                Undo
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default LeadTriageDashboard;
